import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import config from '../Config';
import { authFetch } from '../functions/authFetch';
import { mermadicHostForApp, tierLabel } from '../functions/clusterSwitch';
import { HandleResponse } from '../functions/HandleResponse';
import { useSharedClusterPreview } from '../components/SharedClusterGate';
import './pages.css';

const TIER_CAPS = {
  dev:  { cpu_m: 2000,  memory_mi: 4096  },
  prod: { cpu_m: 7000,  memory_mi: 28672 },
};

const emptyForm = () => ({
  app_name: '',
  replicas: 1,
  container_port: 8080,
  config_map_ref: '',
  secret_ref: '',
  image_name: '',
  url_prefix: '',
  custom_domain: '',
  cpu_limit_m: '',
  memory_limit_mi: '',
});

const APP_NAME_RE = /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/;

/** Mirrors backend sanitization for preview / validation. */
const sanitizeAppName = (name) => {
  if (!name || typeof name !== 'string') return '';
  let s = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-');
  if (s.startsWith('-')) s = s.replace(/^-+/, '');
  if (s.endsWith('-')) s = s.replace(/-+$/, '');
  return s;
};

const validateAppName = (name) => {
  const s = sanitizeAppName(name);
  if (!s) return 'App name is required.';
  if (!APP_NAME_RE.test(s))
    return 'App name must sanitize to lowercase letters, numbers, and hyphens only, starting and ending with a letter or number.';
  return null;
};

/** Copy for the resource-limit banner/popup, varying by classification. */
const limitCopy = (status) => {
  const req = status?.resource_limit_request || {};
  const reqStr = (req.cpu || req.memory)
    ? ` (requesting ${[req.cpu, req.memory].filter(Boolean).join(' / ')})`
    : '';
  if (status?.resource_limit_reason === 'too_large') {
    return {
      title: 'Deployment Request Too Large',
      banner: (
        <>
          <strong>Request too large to schedule.</strong> This deployment has a pod{reqStr} bigger
          than any node in your cluster can hold, so it can never be placed — adding nodes won't
          help. Lower the CPU/memory limit in Advanced Settings.
        </>
      ),
      modalLead: (
        <>has a pod{reqStr} that is <strong>larger than any node</strong> in your cluster can
        provide, so it can never be scheduled — adding more nodes will <strong>not</strong> help.</>
      ),
      modalResolve: (
        <>To resolve this, reduce the pod's CPU/Memory limit in Advanced Settings so it fits your
        cluster's largest node type. Smaller deployments still schedule normally.</>
      ),
    };
  }
  return {
    title: 'Deployment Capacity Reached',
    banner: (
      <>
        <strong>Capacity reached.</strong> Your cluster's on-demand nodes are maxed out and full,
        so one or more pods{reqStr} can't be scheduled and will stay Pending. Stop unused
        deployments, lower replicas/limits, or contact support to raise the limit.
      </>
    ),
    modalLead: (
      <>has one or more pods{reqStr} that can't be placed because your cluster's on-demand nodes are
      at their maximum and full, so they'll stay <em>Pending</em>.</>
    ),
    modalResolve: (
      <>To resolve: stop or delete deployments you no longer need, lower replica counts or per-pod
      limits, or contact support to raise your cluster's limit.</>
    ),
  };
};

const formatRelativeTime = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7)   return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

export default function DeploymentManagement() {
  const navigate = useNavigate();
  const base    = `${config.RUNNER_API}/v1/deployment`;
  const mcrBase = `${config.RUNNER_API}/v1/cdci`;

  // Shared cluster: the screens are browsable, but nothing can be created.
  const isPreview = useSharedClusterPreview();

  // ── cluster tier ─────────────────────────────────────────────────────────────
  const [clusterTier, setClusterTier] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    authFetch(`${config.RUNNER_API}/v1/deployment/get-cluster-tier`, { method: 'GET' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.tier) setClusterTier(d.tier); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── list ─────────────────────────────────────────────────────────────────────
  const [configs, setConfigs]       = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError]   = useState(null);

  // ── panel: null | 'form' | 'view' ────────────────────────────────────────────
  const [panelMode, setPanelMode]   = useState(null);

  // ── view panel ───────────────────────────────────────────────────────────────
  const [viewConfig, setViewConfig] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [status, setStatus]         = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const statusTimerRef = useRef(null);

  // ── resource-limit alert ──────────────────────────────────────────────────────
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const limitDismissedForRef = useRef(null);

  // ── form panel ───────────────────────────────────────────────────────────────
  const [form, setForm]             = useState(emptyForm());
  const [isEditExisting, setIsEditExisting] = useState(false);
  const [configMaps, setConfigMaps] = useState([]);
  const [secrets, setSecrets] = useState([]);
  const [longImages, setLongImages] = useState([]);
  const [verifiedDomains, setVerifiedDomains] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [deploying, setDeploying]   = useState(false);
  const [actionMsg, setActionMsg]   = useState(null);
  const [actionError, setActionError] = useState(null);

  // ── post-deploy hint ─────────────────────────────────────────────────────────
  const [deployedAt, setDeployedAt]     = useState(null);

  // ── stop / delete / redeploy ──────────────────────────────────────────────────
  const [stopping, setStopping]         = useState(false);
  const [deleting, setDeleting]         = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [redeploying, setRedeploying]   = useState(false);

  // ── propagation ───────────────────────────────────────────────────────────────
  const [propagation, setPropagation]               = useState(null);
  const [propagationLoading, setPropagationLoading] = useState(false);
  const propagationTimerRef = useRef(null);

  const [copiedCname, setCopiedCname] = useState(false);

  // ── fetch list ────────────────────────────────────────────────────────────────
  const fetchList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const res = await authFetch(`${base}/list-all-deployment-config`, { method: 'GET' });
      await HandleResponse(res, navigate);
      if (res.ok) {
        setConfigs((await res.json()) || []);
      }
    } catch {
      setListError('Failed to load deployment configs.');
    } finally {
      setListLoading(false);
    }
  }, [base, navigate]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // ── fetch dropdown options ────────────────────────────────────────────────────
  const domainBase = `${config.RUNNER_API}/v1/domain`;
  const secretBase = `${config.RUNNER_API}/v1/secret`;

  const fetchOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      const [cmRes, imgRes, domRes, secRes] = await Promise.all([
        authFetch(`${base}/list-configmap`, { method: 'GET' }),
        authFetch(`${mcrBase}/mcr/images?image_type=long`, { method: 'GET' }),
        authFetch(`${domainBase}/verified`, { method: 'GET' }),
        authFetch(`${secretBase}/list`, { method: 'GET' }),
      ]);
      if (cmRes.ok) setConfigMaps((await cmRes.json()) || []);
      if (secRes.ok) setSecrets((await secRes.json()) || []);
      if (imgRes.ok) {
        const imgs = await imgRes.json();
        setLongImages(Array.isArray(imgs) ? imgs.map(i => i.image_name || i) : []);
      }
      if (domRes.ok) setVerifiedDomains((await domRes.json()) || []);
    } catch { /* dropdowns show empty, user can still proceed */ }
    finally { setOptionsLoading(false); }
  }, [base, mcrBase, domainBase, secretBase]);

  // ── open create form ──────────────────────────────────────────────────────────
  const openCreate = () => {
    setPanelMode('form');
    setIsEditExisting(false);
    setForm(emptyForm());
    setActionMsg(null);
    setActionError(null);
    setDeployedAt(null);
    fetchOptions();
  };

  // ── open existing config ──────────────────────────────────────────────────────
  const openConfig = useCallback(async (appName) => {
    setPanelMode('view');
    setViewConfig(null);
    setStatus(null);
    setPropagation(null);
    setLimitModalOpen(false);
    limitDismissedForRef.current = null;
    setViewLoading(true);
    try {
      const res = await authFetch(`${base}/get-deployment-config?app_name=${encodeURIComponent(appName)}`, { method: 'GET' });
      await HandleResponse(res, navigate);
      if (res.ok) {
        const data = await res.json();
        setViewConfig(data);
        if (data.isDeployed) fetchStatus(appName);
      }
    } catch { }
    finally { setViewLoading(false); }
  }, [base, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStatus = useCallback(async (appName) => {
    setStatusLoading(true);
    try {
      const res = await authFetch(`${base}/get-deployment-status?app_name=${encodeURIComponent(appName)}`, { method: 'GET' });
      if (res.ok) setStatus(await res.json());
    } catch { }
    finally { setStatusLoading(false); }
  }, [base]);

  const checkPropagationDirect = useCallback(async (host) => {
    if (!host) return;
    const url = `https://${host}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    const t0 = performance.now();
    setPropagationLoading(true);
    try {
      await fetch(url, { mode: 'no-cors', signal: controller.signal });
      const latencyMs = Math.round(performance.now() - t0);
      setPropagation({ reachable: true, host, url, latency_ms: latencyMs });
    } catch (err) {
      const latencyMs = Math.round(performance.now() - t0);
      const reason = err.name === 'AbortError' ? 'Timed out' : 'Not reachable';
      setPropagation({ reachable: false, host, url, reason, latency_ms: latencyMs });
    } finally {
      clearTimeout(timeoutId);
      setPropagationLoading(false);
    }
  }, []);

  // Poll propagation every 60 s while a deployed app's panel is open.
  useEffect(() => {
    if (propagationTimerRef.current) clearInterval(propagationTimerRef.current);
    const host = viewConfig?.host;
    if (!viewConfig?.isDeployed || !host) {
      setPropagation(null);
      return;
    }
    checkPropagationDirect(host);
    propagationTimerRef.current = setInterval(() => checkPropagationDirect(host), 60000);
    return () => clearInterval(propagationTimerRef.current);
  }, [viewConfig?.app_name, viewConfig?.isDeployed, viewConfig?.host, checkPropagationDirect]);

  // Poll live status every 30 s while a deployed app's panel is open so the
  // resource-limit flag surfaces even if the user leaves the panel open.
  useEffect(() => {
    if (statusTimerRef.current) clearInterval(statusTimerRef.current);
    const appName = viewConfig?.app_name;
    if (!viewConfig?.isDeployed || !appName) return;
    statusTimerRef.current = setInterval(() => fetchStatus(appName), 30000);
    return () => clearInterval(statusTimerRef.current);
  }, [viewConfig?.app_name, viewConfig?.isDeployed, fetchStatus]);

  // Open the resource-limit popup once per app when it first reports unschedulable.
  useEffect(() => {
    const appName = viewConfig?.app_name;
    if (status?.resource_limit_reached && appName && limitDismissedForRef.current !== appName) {
      setLimitModalOpen(true);
    }
  }, [status?.resource_limit_reached, viewConfig?.app_name]);

  // ── switch view → edit ────────────────────────────────────────────────────────
  const switchToEdit = () => {
    if (!viewConfig) return;
    setForm({
      app_name:        viewConfig.app_name        || '',
      replicas:        viewConfig.replicas         ?? 1,
      container_port:  viewConfig.container_port   ?? 8080,
      config_map_ref:  viewConfig.configMapRef     || '',
      secret_ref:      viewConfig.secretRef        || '',
      image_name:      viewConfig.image_name       || '',
      url_prefix:      viewConfig.url_prefix       || '',
      custom_domain:   viewConfig.custom_domain    || '',
      cpu_limit_m:     viewConfig.cpu_limit_m      != null ? String(viewConfig.cpu_limit_m)     : '',
      memory_limit_mi: viewConfig.memory_limit_mi  != null ? String(viewConfig.memory_limit_mi) : '',
    });
    setAdvancedOpen(viewConfig.cpu_limit_m != null || viewConfig.memory_limit_mi != null);
    setIsEditExisting(true);
    setPanelMode('form');
    setActionMsg(null);
    setActionError(null);
    fetchOptions();
  };

  const updateForm = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const buildPayload = () => ({
    appName:        form.app_name,
    replicas:       Number(form.replicas),
    imageName:      form.image_name,
    containerPort:  Number(form.container_port),
    configMapRef:   form.config_map_ref || '',
    secretRef:      form.secret_ref     || '',
    urlPrefix:      form.url_prefix     || '',
    cpuLimitM:      form.cpu_limit_m     !== '' ? Number(form.cpu_limit_m)     : null,
    memoryLimitMi:  form.memory_limit_mi !== '' ? Number(form.memory_limit_mi) : null,
    customDomain:  form.custom_domain  || '',
  });

  // ── save config ───────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (isPreview) return;
    const nameErr = validateAppName(form.app_name);
    if (nameErr) { setActionError(nameErr); return; }
    const submittedName = form.app_name;
    setSaving(true);
    setActionMsg(null);
    setActionError(null);
    try {
      const res = await authFetch(`${base}/save-deployment-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      });
      await HandleResponse(res, navigate);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if (data.app_name) {
          setForm(f => ({ ...f, app_name: data.app_name }));
          if (submittedName.trim() !== data.app_name) {
            setActionMsg(`Config saved as "${data.app_name}" (name adjusted for DNS).`);
          } else {
            setActionMsg('Config saved successfully.');
          }
        } else {
          setActionMsg('Config saved successfully.');
        }
        fetchList();
      } else {
        setActionError(data.detail || 'Save failed.');
      }
    } catch { setActionError('Save failed.'); }
    finally { setSaving(false); }
  };

  // ── deploy (save → deploy-app → deploy-ingress) ───────────────────────────────
  const handleDeploy = async () => {
    if (isPreview) return;
    const nameErr = validateAppName(form.app_name);
    if (nameErr)                 { setActionError(nameErr); return; }
    if (!form.image_name.trim()) { setActionError('Image is required.'); return; }
    if (form.url_prefix && form.url_prefix.includes('.')) {
      setActionError('URL prefix must not contain a dot. Enter a subdomain prefix only (e.g. "api").'); return;
    }

    setDeploying(true);
    setActionMsg(null);
    setActionError(null);
    try {
      // 1. save config
      const saveRes = await authFetch(`${base}/save-deployment-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      });
      await HandleResponse(saveRes, navigate);
      const saveData = await saveRes.json().catch(() => ({}));
      if (!saveRes.ok) {
        setActionError(saveData.detail || 'Save failed.');
        return;
      }

      const deployedAppName = saveData.app_name || sanitizeAppName(form.app_name);
      if (saveData.app_name) {
        setForm(f => ({ ...f, app_name: saveData.app_name }));
      }

      // 2. deploy app
      const deployRes = await authFetch(
        `${base}/deploy-app?app_name=${encodeURIComponent(deployedAppName)}`,
        { method: 'POST' },
      );
      await HandleResponse(deployRes, navigate);
      if (!deployRes.ok) {
        setActionError((await deployRes.json().catch(() => ({}))).detail || 'Deploy app failed.');
        return;
      }

      // 3. redeploy ingress
      await authFetch(`${base}/deploy-ingress`, { method: 'POST' });

      setIsEditExisting(true);
      setDeployedAt(Date.now());
      fetchList();
      await openConfig(deployedAppName);
    } catch { setActionError('Deployment failed.'); }
    finally { setDeploying(false); }
  };

  // ── redeploy ─────────────────────────────────────────────────────────────────
  const handleRedeploy = async () => {
    if (!viewConfig) return;
    setRedeploying(true);
    try {
      const res = await authFetch(
        `${base}/redeploy?app_name=${encodeURIComponent(viewConfig.app_name)}`,
        { method: 'POST' },
      );
      await HandleResponse(res, navigate);
      if (res.ok) {
        await fetchStatus(viewConfig.app_name);
      }
    } catch { }
    finally { setRedeploying(false); }
  };

  // ── download pod logs ────────────────────────────────────────────────────────
  const [logsDownloading, setLogsDownloading] = useState(false);

  const handleGetLogs = async () => {
    if (!viewConfig) return;
    setLogsDownloading(true);
    try {
      const res = await authFetch(
        `${base}/get-deployments-log?app_name=${encodeURIComponent(viewConfig.app_name)}`,
        { method: 'GET' },
      );
      await HandleResponse(res, navigate);
      if (!res.ok) return;
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${viewConfig.app_name}-logs.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { }
    finally { setLogsDownloading(false); }
  };

  // ── stop deployment ───────────────────────────────────────────────────────────
  const handleStop = async () => {
    if (!viewConfig) return;
    setStopping(true);
    try {
      const res = await authFetch(`${base}/stop-deployment?app_name=${encodeURIComponent(viewConfig.app_name)}`, { method: 'POST' });
      await HandleResponse(res, navigate);
      if (res.ok) {
        // Redeploy ingress to apply rule removal
        await authFetch(`${base}/deploy-ingress`, { method: 'POST' });
        await openConfig(viewConfig.app_name);
        fetchList();
      }
    } catch { }
    finally { setStopping(false); }
  };

  // ── delete deployment config ──────────────────────────────────────────────────
  const handleDeleteConfig = async () => {
    if (!viewConfig) return;
    setDeleteConfirm(false);
    setDeleting(true);
    try {
      const res = await authFetch(`${base}/delete-deployment-config?app_name=${encodeURIComponent(viewConfig.app_name)}`, { method: 'DELETE' });
      await HandleResponse(res, navigate);
      if (res.ok) {
        if (viewConfig.isDeployed) {
          await authFetch(`${base}/deploy-ingress`, { method: 'POST' });
        }
        setPanelMode(null);
        setViewConfig(null);
        fetchList();
      }
    } catch { }
    finally { setDeleting(false); }
  };

  // ── render propagation ────────────────────────────────────────────────────────
  const renderPropagation = () => (
    <div className="dm-propagation-row">
      <span className="dm-propagation-label">Propagation</span>
      {propagationLoading && !propagation ? (
        <span className="dm-propagation-checking">Checking…</span>
      ) : propagation ? (
        propagation.reachable ? (
          <span className="dm-propagation-ok">
            ✓ Reachable &nbsp;·&nbsp; {propagation.latency_ms} ms
          </span>
        ) : (
          <span className="dm-propagation-fail">
            ✗ Not reachable
            {propagation.reason ? ` — ${propagation.reason}` : ''}
          </span>
        )
      ) : null}
      {(propagation || propagationLoading) && (
        <button
          className="dm-propagation-refresh"
          onClick={() => checkPropagationDirect(viewConfig?.host)}
          disabled={propagationLoading}
          title="Re-check now"
        >↺</button>
      )}
    </div>
  );

  // ── render status ─────────────────────────────────────────────────────────────
  const renderStatus = () => {
    if (statusLoading) return <p className="dm-hint">Loading live status…</p>;
    if (!status) return null;
    const allReady = status.ready_replicas >= status.desired_replicas && status.desired_replicas > 0;
    return (
      <div className="dm-status-box">
        <div className="dm-status-header">
          <span className="dm-status-title">Live Status</span>
          <div className="dm-status-header-right">
            <span className={`dm-status-badge ${allReady ? 'dm-badge-ok' : 'dm-badge-warn'}`}>
              {allReady ? 'Healthy' : 'Degraded'}
            </span>
            <button
              className="dm-btn-logs"
              onClick={handleGetLogs}
              disabled={logsDownloading}
              title="Download logs from all pods as a zip file"
            >
              {logsDownloading ? 'Downloading…' : '⬇ Logs'}
            </button>
          </div>
        </div>
        {status.resource_limit_reached && (
          <div className={`dm-limit-banner${status.resource_limit_reason === 'too_large' ? ' dm-limit-banner--hard' : ''}`}>
            <span className="dm-limit-banner-icon">⚠</span>
            <div>
              {limitCopy(status).banner}{' '}
              Batch and cron jobs run on separate capacity and are unaffected.
            </div>
          </div>
        )}
        <div className="dm-replica-grid">
          <div className="dm-replica-cell"><span className="dm-replica-num">{status.desired_replicas ?? 0}</span><span className="dm-replica-lbl">Desired</span></div>
          <div className="dm-replica-cell"><span className="dm-replica-num">{status.ready_replicas ?? 0}</span><span className="dm-replica-lbl">Ready</span></div>
          <div className="dm-replica-cell"><span className="dm-replica-num">{status.available_replicas ?? 0}</span><span className="dm-replica-lbl">Available</span></div>
        </div>
        {status.pods && status.pods.length > 0 && (
          <div className="dm-pods">
            <p className="dm-pods-label">Pods</p>
            {status.pods.map((pod, i) => (
              <div key={i} className={`dm-pod-card ${pod.phase === 'Running' && pod.ready ? 'dm-pod-running' : 'dm-pod-warn'}`}>
                <span className="dm-pod-name">{pod.name}</span>
                <span className="dm-pod-phase">{pod.phase}</span>
                {pod.containers && pod.containers.map((c, j) => (
                  <span key={j} className="dm-pod-container">{c.name}: {c.state} (restarts: {c.restart_count})</span>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ── render view panel ─────────────────────────────────────────────────────────
  const renderView = () => {
    if (viewLoading) return <p className="dm-hint">Loading…</p>;
    if (!viewConfig) return null;
    const v = viewConfig;
    return (
      <div className="dm-view-panel">
        <div className="dm-panel-header">
          <div className="dm-panel-header-left">
            <h3 className="dm-panel-title">{v.app_name}</h3>
            {(v.host || v.url) && (
              <a className="dm-url" href={`https://${v.host || v.url}`} target="_blank" rel="noreferrer">
                {v.host || v.url}
              </a>
            )}
          </div>
          <div className="dm-panel-actions">
            <button className="cm-btn cm-btn-ghost cm-btn-sm" onClick={switchToEdit} disabled={stopping || deleting}>Edit</button>
            <button className="cm-btn cm-btn-ghost cm-btn-sm" onClick={() => { setPanelMode(null); setViewConfig(null); }}>✕</button>
          </div>
        </div>
        <div className="dm-config-grid">
          <span className="dm-cfg-label">Image</span>       <span className="dm-cfg-val"><code>{v.image_name}</code></span>
          <span className="dm-cfg-label">ConfigMap</span>   <span className="dm-cfg-val"><code>{v.configMapRef || '— none —'}</code></span>
          <span className="dm-cfg-label">Secret</span>      <span className="dm-cfg-val"><code>{v.secretRef || '— none —'}</code></span>
          <span className="dm-cfg-label">Replicas</span>    <span className="dm-cfg-val">{v.replicas}</span>
          <span className="dm-cfg-label">Port</span>        <span className="dm-cfg-val">{v.container_port}</span>
          <span className="dm-cfg-label">Host</span>        <span className="dm-cfg-val"><code>{v.host}</code></span>
          {v.custom_domain && (
            <><span className="dm-cfg-label">Custom Domain</span>
            <span className="dm-cfg-val"><code>{v.url_prefix}.{v.custom_domain}</code></span></>
          )}
          <span className="dm-cfg-label">Deployment</span>  <span className="dm-cfg-val"><code>{v.deployment_name}</code></span>
          <span className="dm-cfg-label">Last Deployed</span>
          <span className="dm-cfg-val">
            {v.last_deployed
              ? <span title={new Date(v.last_deployed).toLocaleString()}>{formatRelativeTime(v.last_deployed)}</span>
              : <span style={{ color: '#9aab9a' }}>Never</span>}
          </span>
        </div>

        {v.url_prefix && v.custom_domain && (
          <div className="dm-cname-box">
            <p className="dm-cname-title">
              <span className="dm-cname-icon">🔗</span> DNS CNAME Required
            </p>
            <p className="dm-cname-desc">
              Add this record with your DNS provider to route your custom host through Mermadic.
              When failover occurs, only the value below changes — your CNAME stays the same.
            </p>
            <div className="dm-cname-rows">
              <div className="dm-cname-row">
                <span className="dm-cname-key">Type</span>
                <span className="dm-cname-val">CNAME</span>
              </div>
              <div className="dm-cname-row">
                <span className="dm-cname-key">Record</span>
                <span className="dm-cname-val"><code>{v.url_prefix}</code></span>
              </div>
              <div className="dm-cname-row">
                <span className="dm-cname-key">Value</span>
                <span className="dm-cname-val">
                  <span className="dm-cname-val-row">
                    <code className="dm-cname-alb">{v.mermadic_host}</code>
                    <button
                      className="dm-cname-copy"
                      title="Copy"
                      onClick={() => {
                        navigator.clipboard.writeText(v.mermadic_host);
                        setCopiedCname(true);
                        setTimeout(() => setCopiedCname(false), 2000);
                      }}
                    >{copiedCname ? '✓' : '⎘'}</button>
                  </span>
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="dm-view-actions">
          {v.isDeployed && (
            <button className="dm-btn-redeploy" onClick={handleRedeploy} disabled={stopping || deleting || redeploying}>
              {redeploying ? 'Redeploying…' : '↺ Redeploy'}
            </button>
          )}
          {v.isDeployed && (
            <button className="dm-btn-stop" onClick={handleStop} disabled={stopping || deleting || redeploying}>
              {stopping ? 'Stopping…' : '⏹ Stop'}
            </button>
          )}
          <button className="dm-btn-delete" onClick={() => setDeleteConfirm(true)} disabled={stopping || deleting || redeploying}>
            {deleting ? 'Deleting…' : 'Delete Config'}
          </button>
        </div>

        {v.isDeployed && deployedAt && (Date.now() - deployedAt < 10 * 60 * 1000) && (
          <p className="dm-dns-hint">
            DNS changes may take up to 5 minutes to propagate. If the site isn't reachable yet, wait a moment and try again.
          </p>
        )}

        {v.isDeployed && renderPropagation()}
        {v.isDeployed && renderStatus()}

        {limitModalOpen && (() => {
          const c = limitCopy(status);
          const dismiss = () => { limitDismissedForRef.current = v.app_name; setLimitModalOpen(false); };
          return (
            <div className="cm-modal-overlay" onClick={dismiss}>
              <div className="cm-modal cm-modal--limit" onClick={e => e.stopPropagation()}>
                <div className="dm-limit-modal-head">
                  <span className="dm-limit-modal-icon">⚠</span>
                  <h4 className="cm-modal-title">{c.title}</h4>
                </div>
                <p className="cm-modal-body">
                  <strong>{v.app_name}</strong> {c.modalLead}
                </p>
                {status?.resource_limit_message && (
                  <div className="dm-limit-modal-detail">
                    <div className="dm-limit-detail-label">
                      Current cluster capacity — how many of your cluster's nodes can currently fit this deployment:
                    </div>
                    <code>{status.resource_limit_message}</code>
                  </div>
                )}
                <p className="cm-modal-body">
                  {c.modalResolve} Batch jobs and cron jobs use separate capacity and are unaffected.
                </p>
                <div className="cm-modal-buttons">
                  <button className="cm-btn cm-btn-primary" onClick={dismiss}>Got it</button>
                </div>
              </div>
            </div>
          );
        })()}

        {deleteConfirm && (
          <div className="cm-modal-overlay" onClick={() => setDeleteConfirm(false)}>
            <div className="cm-modal" onClick={e => e.stopPropagation()}>
              <h4 className="cm-modal-title">Delete Deployment Config</h4>
              <p className="cm-modal-body">
                Are you sure you want to delete <strong>{v.app_name}</strong>?
                {v.isDeployed && ' This will also stop the running deployment and remove its ingress rule.'}
              </p>
              <div className="cm-modal-buttons">
                <button className="cm-btn cm-btn-ghost" onClick={() => setDeleteConfirm(false)}>Cancel</button>
                <button className="dm-btn-delete" onClick={handleDeleteConfig}>Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── render form panel ─────────────────────────────────────────────────────────
  const renderForm = () => (
    <div className="dm-form-panel">
      <div className="dm-panel-header">
        <h3 className="dm-panel-title">{isEditExisting ? `Edit: ${form.app_name}` : 'New Deployment Config'}</h3>
        <button className="cm-btn cm-btn-ghost cm-btn-sm" onClick={() => { setPanelMode(null); setActionMsg(null); setActionError(null); }}>✕</button>
      </div>

      <div className="dm-form-body">
        <div className="dm-field">
          <label className="dm-label">App Name {isEditExisting && <span className="dm-label-hint">(read-only)</span>}</label>
          <input
            className={`cm-input dm-input dm-input-md${isEditExisting ? ' dm-input-readonly' : ''}`}
            value={form.app_name}
            onChange={e => !isEditExisting && updateForm('app_name', e.target.value)}
            readOnly={isEditExisting}
            placeholder="my-api"
          />
          {!isEditExisting && form.app_name && (
            validateAppName(form.app_name)
              ? <span className="dm-field-err">{validateAppName(form.app_name)}</span>
              : (
                <span className="dm-field-hint">
                  Saved as <code>{sanitizeAppName(form.app_name)}</code>
                  {' · '}
                  Host:{' '}
                  <code>
                    {mermadicHostForApp(sanitizeAppName(form.app_name))}
                  </code>
                </span>
              )
          )}
        </div>

        <div className="dm-field">
          <label className="dm-label">
            ConfigMap <span className="dm-label-hint">(optional){optionsLoading ? ' — loading…' : ''}</span>
          </label>
          <select
            className="dm-select dm-select-md"
            value={form.config_map_ref}
            onChange={e => updateForm('config_map_ref', e.target.value)}
          >
            <option value="">— none —</option>
            {configMaps.map(cm => <option key={cm} value={cm}>{cm}</option>)}
          </select>
        </div>

        <div className="dm-field">
          <label className="dm-label">
            Secret <span className="dm-label-hint">(optional){optionsLoading ? ' — loading…' : ''}</span>
          </label>
          <select
            className="dm-select dm-select-md"
            value={form.secret_ref}
            onChange={e => updateForm('secret_ref', e.target.value)}
          >
            <option value="">— none —</option>
            {secrets.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="dm-field">
          <label className="dm-label">
            MCR Image {optionsLoading && <span className="dm-label-hint">loading…</span>}
          </label>
          <select
            className="dm-select dm-select-md"
            value={form.image_name}
            onChange={e => updateForm('image_name', e.target.value)}
          >
            <option value="">— select an image —</option>
            {longImages.map(img => <option key={img} value={img}>{img}</option>)}
          </select>
        </div>

        <div className="dm-row2">
          <div className="dm-field">
            <label className="dm-label">Replicas</label>
            <input
              className="cm-input dm-input dm-input-sm"
              type="number"
              min="1"
              value={form.replicas}
              onChange={e => updateForm('replicas', e.target.value)}
            />
          </div>
          <div className="dm-field">
            <label className="dm-label">Container Port</label>
            <input
              className="cm-input dm-input dm-input-sm dm-no-spinner"
              type="number"
              min="1"
              max="65535"
              value={form.container_port}
              onChange={e => updateForm('container_port', e.target.value)}
            />
          </div>
        </div>

        {/* ── Custom domain host (optional) ── */}
        <div className="dm-field">
          <label className="dm-label">
            Custom Domain Host <span className="dm-label-hint">(optional — requires a verified domain)</span>
          </label>
          <div className="dm-host-row">
            <input
              className="cm-input dm-input dm-host-prefix"
              placeholder="prefix"
              value={form.url_prefix}
              onChange={e => updateForm('url_prefix', e.target.value.replace(/\./g, ''))}
              maxLength={63}
            />
            <span className="dm-host-dot">.</span>
            <select
              className="dm-select dm-host-domain"
              value={form.custom_domain}
              onChange={e => updateForm('custom_domain', e.target.value)}
            >
              <option value="">— None —</option>
              {verifiedDomains.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          {form.url_prefix && form.custom_domain && (
            <span className="dm-field-hint">
              Host will be: <code>{form.url_prefix}.{form.custom_domain}</code>
            </span>
          )}
          {!form.url_prefix && !form.custom_domain && (
            <span className="dm-field-hint">
              Default: <code>{mermadicHostForApp(sanitizeAppName(form.app_name) || 'app')}</code>
            </span>
          )}
          {verifiedDomains.length === 0 && (
            <span className="dm-field-hint">
              No verified domains yet — add one under <strong>Apps → Domains</strong>.
            </span>
          )}
        </div>

        {/* ── Advanced Settings (resource limits) ── */}
        {clusterTier && TIER_CAPS[clusterTier] && (() => {
          const caps = TIER_CAPS[clusterTier];
          return (
            <div className="dm-field">
              <button
                type="button"
                className="dm-advanced-toggle"
                onClick={() => setAdvancedOpen(o => !o)}
              >
                {advancedOpen ? '▾' : '▸'} Advanced Settings
              </button>
              {advancedOpen && (
                <div className="dm-advanced-body">
                  <p className="dm-field-hint">
                    Resource limits for each pod. Leave blank to use cluster defaults (500m CPU / 512Mi memory).
                    Max for <strong>{tierLabel(clusterTier)}</strong> tier: {caps.cpu_m}m CPU / {caps.memory_mi}Mi memory.
                  </p>
                  <div className="dm-row2">
                    <div className="dm-field">
                      <label className="dm-label">CPU Limit (millicores)</label>
                      <input
                        className="cm-input dm-input dm-input-sm dm-no-spinner"
                        type="number"
                        min="100"
                        max={caps.cpu_m}
                        step="100"
                        placeholder={`e.g. 500 (max ${caps.cpu_m})`}
                        value={form.cpu_limit_m}
                        onChange={e => updateForm('cpu_limit_m', e.target.value)}
                      />
                    </div>
                    <div className="dm-field">
                      <label className="dm-label">Memory Limit (MiB)</label>
                      <input
                        className="cm-input dm-input dm-input-sm dm-no-spinner"
                        type="number"
                        min="128"
                        max={caps.memory_mi}
                        step="128"
                        placeholder={`e.g. 512 (max ${caps.memory_mi})`}
                        value={form.memory_limit_mi}
                        onChange={e => updateForm('memory_limit_mi', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {actionError && <p className="cm-error">{actionError}</p>}
        {actionMsg   && <p className="cm-success">{actionMsg}</p>}

        <div className="dm-form-buttons">
          <button
            className="dm-btn-secondary"
            onClick={handleSave}
            disabled={isPreview || saving || deploying}
            title={isPreview ? 'Needs a dedicated cluster' : undefined}
          >
            {saving ? 'Saving…' : 'Save Config'}
          </button>
          <button
            className="dm-btn-deploy"
            onClick={handleDeploy}
            disabled={isPreview || saving || deploying}
            title={isPreview ? 'Needs a dedicated cluster' : undefined}
          >
            {deploying ? 'Deploying…' : '▶ Deploy App'}
          </button>
        </div>

        {isPreview && (
          <p className="dm-field-hint">
            You are on the shared cluster, so this config cannot be saved or deployed.{' '}
            <Link className="inst-link" to="/cluster">Create your own cluster</Link> to run it.
            Batch and cron jobs work right now on shared.
          </p>
        )}
      </div>
    </div>
  );

  // ── main render ───────────────────────────────────────────────────────────────
  return (
    <div className="cm-page">
      <h2 className="cm-title">Deployments</h2>
      <p className="cm-subtitle">Manage and deploy your application configurations to Kubernetes.</p>

      <div className="cm-layout">
        {/* ── sidebar ── */}
        <div className="cm-sidebar">
          <div className="cm-sidebar-header">
            <span>Deployments</span>
            <button className="cm-btn cm-btn-primary cm-btn-sm" onClick={openCreate}>+ New</button>
          </div>

          {listLoading && <p className="cm-hint" style={{ padding: '0.5rem 1rem' }}>Loading…</p>}
          {listError   && <p className="cm-error" style={{ padding: '0.5rem 1rem' }}>{listError}</p>}
          {!listLoading && configs.length === 0 && <p className="cm-hint" style={{ padding: '0.5rem 1rem' }}>No configs yet.</p>}

          <ul className="cm-list">
            {configs.map(({ app_name, isDeployed, host, last_deployed }) => (
              <li
                key={app_name}
                className={`cm-list-item cm-list-item-stacked ${viewConfig?.app_name === app_name || (panelMode === 'form' && isEditExisting && form.app_name === app_name) ? 'active' : ''}`}
                onClick={() => openConfig(app_name)}
              >
                <div className="cm-list-item-main">
                  <span className="cm-list-name">{app_name}</span>
                  <span className={`dm-deploy-dot ${isDeployed ? 'dm-dot-deployed' : 'dm-dot-pending'}`} title={isDeployed ? 'Deployed' : 'Not deployed'} />
                </div>
                {last_deployed && (
                  <div className="dm-sidebar-meta" title={new Date(last_deployed).toLocaleString()}>
                    Deployed {formatRelativeTime(last_deployed)}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* ── right panel ── */}
        <div className="cm-detail" style={{ minHeight: 480 }}>
          {panelMode === 'form' ? renderForm()
           : panelMode === 'view' ? renderView()
           : (
            <div className="cm-empty-state">
              <p>Select a deployment to view its config,<br />or click <strong>+ New</strong> to create one.</p>
            </div>
          )}
        </div>

        {/* ── GUIDE: instruction panel ── */}
        <div className="page-inst-panel page-inst-panel--wide">
          <h4>Field Guide</h4>
          <div className="inst-field">
            <span className="inst-field-name">App Name</span>
            Lowercase letters, numbers, hyphens. Becomes the Kubernetes Deployment and Service name.
          </div>
          <div className="inst-field">
            <span className="inst-field-name">ConfigMap</span>
            Optional. Injects env vars into your container. Create one first in <Link className="inst-link" to="/deployment/config">ConfigMaps</Link>.
          </div>
          <div className="inst-field">
            <span className="inst-field-name">Secret</span>
            Optional. Same injection, for values you should not be able to read back — create one in <Link className="inst-link" to="/deployment/secrets">Secrets</Link>. Wins over the ConfigMap on a shared key.
          </div>
          <div className="inst-field">
            <span className="inst-field-name">MCR Image</span>
            The image built by your CDCI pipeline. Built images appear here automatically.
          </div>
          <div className="inst-field">
            <span className="inst-field-name">Replicas</span>
            Number of pods to run. Start with <code>1</code>; scale up for high-availability.
          </div>
          <div className="inst-field">
            <span className="inst-field-name">Container Port</span>
            The port your app listens on inside the container (e.g. <code>8080</code>, <code>3000</code>).
          </div>
          <hr className="inst-divider" />
          <h4>Custom Domain Host</h4>
          <div className="inst-field">
            <span className="inst-field-name">Prefix</span>
            Subdomain label, no dots (e.g. <code>api</code>, <code>app</code>).
          </div>
          <div className="inst-field">
            <span className="inst-field-name">Domain</span>
            A verified domain from <Link className="inst-link" to="/deployment/domains">Domains</Link>. Combined result: <code>prefix.example.com</code>.
          </div>
          <p>Leave both blank to use the default <code>cluster-app.mermadicrunner.com</code> host.</p>
        </div>
      </div>
    </div>
  );
}
